import { Alert, Button, Spinner } from '@cartly/ui';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { useSubmit, useUrlToken } from '../../app/hooks';
import { useSession } from '../../app/session';
import { AuthLayout } from '../../layouts/AuthLayout';

export function VerifyEmailPage() {
  const token = useUrlToken();
  const { state, refresh } = useSession();
  const [result, setResult] = useState<'pending' | 'ok' | string>('pending');
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post('/auth/verify-email', { token })
      .then(async () => {
        setResult('ok');
        if (state.status === 'active') await refresh();
      })
      .catch((err: unknown) => setResult(errorMessage(err)));
  }, [token, state.status, refresh]);

  const next = state.status === 'active' ? '/' : '/login';
  return (
    <AuthLayout title="Email verification">
      {!token ? (
        <Alert tone="critical">This verification link is incomplete.</Alert>
      ) : result === 'pending' ? (
        <div className="row">
          <Spinner /> Verifying…
        </div>
      ) : result === 'ok' ? (
        <div className="stack">
          <Alert tone="success" title="Email verified">
            Thanks — your email address is confirmed.
          </Alert>
          <Link to={next} className="btn btn--primary btn--block">
            Continue
          </Link>
        </div>
      ) : (
        <div className="stack">
          <Alert tone="critical">{result}</Alert>
          <Link to={next}>Continue</Link>
        </div>
      )}
    </AuthLayout>
  );
}

interface InvitePreview {
  tenantName: string;
  email: string;
  roleName: string;
  expiresAt: string;
}

const PENDING_INVITE_KEY = 'cartly.pendingInvite';

/** Keeps the invite token across a sign-in/registration detour within this tab. */
export function rememberInviteToken(token: string | null) {
  try {
    if (token) sessionStorage.setItem(PENDING_INVITE_KEY, token);
    else sessionStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    // Storage unavailable: the user can reopen the link from the email.
  }
}

function recallInviteToken(): string | null {
  try {
    return sessionStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return null;
  }
}

export function AcceptInvitePage() {
  const urlToken = useUrlToken();
  const [token] = useState(() => urlToken ?? recallInviteToken());
  const { state, refresh } = useSession();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { pending, error, run } = useSubmit();

  useEffect(() => {
    if (!token) return;
    rememberInviteToken(token);
    api
      .post<InvitePreview>('/invitations/preview', { token })
      .then(setPreview)
      .catch((err: unknown) => setLoadError(errorMessage(err)));
  }, [token]);

  const accept = async () => {
    const result = await run(() =>
      api.post<{ tenantId: string }>('/invitations/accept', { token }),
    );
    if (!result) return;
    rememberInviteToken(null);
    await refresh();
    navigate('/', { replace: true });
  };

  const signedInAs = state.status === 'active' ? state.session.user.email : null;

  return (
    <AuthLayout title="Join your team">
      {!token ? (
        <Alert tone="critical">This invitation link is incomplete.</Alert>
      ) : loadError ? (
        <Alert tone="critical">{loadError}</Alert>
      ) : !preview ? (
        <div className="row">
          <Spinner /> Loading invitation…
        </div>
      ) : (
        <div className="stack">
          <p>
            You've been invited to join <strong>{preview.tenantName}</strong> as{' '}
            <strong>{preview.roleName}</strong>.
          </p>
          {error && <Alert tone="critical">{error}</Alert>}
          {signedInAs === null ? (
            <>
              <Alert tone="info">
                Sign in or create an account with <strong>{preview.email}</strong> to accept.
              </Alert>
              <div className="row">
                <Link to="/login" state={{ from: '/accept-invite' }} className="btn btn--primary">
                  Sign in
                </Link>
                <Link
                  to="/register"
                  state={{ from: '/accept-invite' }}
                  className="btn btn--secondary"
                >
                  Create account
                </Link>
              </div>
            </>
          ) : signedInAs !== preview.email ? (
            <Alert tone="warning">
              You're signed in as {signedInAs}, but this invitation was sent to {preview.email}.
              Sign in with that address to accept it.
            </Alert>
          ) : (
            <Button variant="primary" block loading={pending} onClick={() => void accept()}>
              Accept invitation
            </Button>
          )}
        </div>
      )}
    </AuthLayout>
  );
}
